import axios from 'axios';
import config from '../config';

const axiosInstance = axios.create({
  baseURL: config.apiUrl,
});

let clerkSession = null;

export const setClerkSession = (session) => {
  clerkSession = session;
};

// Keep backward compat
export const setClerkTokenGetter = (getter) => {};

// getToken() can hang indefinitely when the Clerk session has gone stale
// (tab left open, laptop asleep): it tries to refresh against Clerk and
// never settles. Cap it so a dead session degrades to a fast 403 (which
// the response interceptor below handles) instead of pages that spin
// forever without ever hitting the API.
const getTokenWithTimeout = (opts = {}, ms = 5000) =>
  Promise.race([
    clerkSession.getToken(opts),
    new Promise((resolve) => setTimeout(() => resolve(null), ms)),
  ]);

axiosInstance.interceptors.request.use(
  async (requestConfig) => {
    if (clerkSession) {
      try {
        const token = await getTokenWithTimeout();
        if (token) {
          requestConfig.headers.Authorization = `Bearer ${token}`;
        }
      } catch (err) {
        console.error('[axios] Failed to get token:', err);
      }
    }
    return requestConfig;
  },
  (error) => Promise.reject(error)
);

// One recovery attempt on auth failures: force a fresh token (skip
// Clerk's cache) and replay the request. If that still fails, the
// session is truly gone - bounce to login rather than leaving every
// page to fail quietly.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config: reqConfig } = error;
    const status = response?.status;
    if ((status === 401 || status === 403) && clerkSession && reqConfig && !reqConfig._authRetried) {
      reqConfig._authRetried = true;
      try {
        const token = await getTokenWithTimeout({ skipCache: true });
        if (token) {
          reqConfig.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(reqConfig);
        }
      } catch (err) {
        console.error('[axios] Token refresh failed:', err);
      }
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
